import React, { useEffect, useMemo, useRef } from "react"
import { AbsoluteFill, Freeze, Sequence, getRemotionEnvironment, useCurrentFrame, useVideoConfig } from "remotion"
import type { SceneMetadata, SceneChunk } from "../../schemas"
import { VideoTrack } from "./VideoTrack"
import { faceAt, faceCenterXAt, faceCropRect, layoutPanelRects, outgoingTransitionStyle, resolveLayoutSpans, type LayoutSpan } from "../../utils/layout"

interface SceneReframeLayerProps {
  sceneMetadata: SceneMetadata
  src: string
}

/**
 * SceneReframeLayer — reads V4 sceneMetadata and applies per-chunk reframing.
 *
 * Each scene renders in its own Sequence. At a boundary the outgoing scene stays mounted for
 * the transition, frozen on its last frame and drawn over the incoming scene while it fades /
 * slides / zooms away, so there is never a black frame between layouts. Every visual copy of
 * the source is muted; ClipComposition plays the source audio once via SourceAudio. In the Player
 * the layer loads the source once, hidden, and every panel of every scene is a canvas mirroring that
 * <video>: Chrome allows six connections per host, so a <video> per panel starves other media, and a
 * <video> per scene would have to start (and buffer) at each scene boundary. Renders keep one
 * OffthreadVideo per panel.
 *
 * Modes:
 *  - "wide":        full source scaled to fit width, blurred background top/bottom
 *  - "single":      height-fill crop centered on face_center_x_pct
 *  - "dual":        split-screen top/bottom, each panel cropped on its respective face
 *  - "triple":      one face on top, two below
 *  - "quad":        2x2 grid of faces
 *  - "screenshare": wide screen with the speaker's face picture-in-picture
 */
export const SceneReframeLayer: React.FC<SceneReframeLayerProps> = ({ sceneMetadata, src }) => {
  const { fps, durationInFrames } = useVideoConfig()
  const { isPlayer } = getRemotionEnvironment()
  const videoRef = useRef<HTMLVideoElement>(null)

  const spans = useMemo(
    () => resolveLayoutSpans(sceneMetadata, fps, durationInFrames),
    [sceneMetadata, fps, durationInFrames],
  )

  // Later scenes first in the DOM, so an outgoing scene's transition tail draws on top.
  return (
    <AbsoluteFill style={{ backgroundColor: "#000" }}>
      {isPlayer && (
        <AbsoluteFill style={{ opacity: 0 }}>
          <VideoTrack src={src} muted videoRef={videoRef} />
        </AbsoluteFill>
      )}
      {spans.map((span, i) => {
        const next = spans[i + 1]
        const holdFrames = span.endFrame - span.startFrame
        const tailFrames = next?.transitionIn.frames ?? 0
        return (
          <Sequence
            key={span.chunk.id}
            from={span.startFrame}
            durationInFrames={holdFrames + tailFrames}
            premountFor={isPlayer ? fps : 0}
            name={`Scene ${span.chunk.mode}`}
          >
            <SpanLayer
              span={span}
              holdFrames={holdFrames}
              tailFrames={tailFrames}
              outgoing={next?.transitionIn.type ?? "cut"}
              source={sceneMetadata.source}
              src={src}
              videoRef={videoRef}
            />
          </Sequence>
        )
      }).reverse()}
    </AbsoluteFill>
  )
}

const SpanLayer: React.FC<{
  span: LayoutSpan
  holdFrames: number
  tailFrames: number
  outgoing: LayoutSpan["transitionIn"]["type"]
  source: SceneMetadata["source"]
  src: string
  videoRef: React.RefObject<HTMLVideoElement | null>
}> = ({ span, holdFrames, tailFrames, outgoing, source, src, videoRef }) => {
  const frame = useCurrentFrame()
  const { width, height } = useVideoConfig()
  const { isPlayer } = getRemotionEnvironment()
  const inTail = frame >= holdFrames
  const style = inTail
    ? outgoingTransitionStyle(outgoing, (frame - holdFrames + 1) / (tailFrames + 1))
    : { opacity: 1, translateXPct: 0, scale: 1 }

  return (
    <AbsoluteFill
      style={{
        opacity: style.opacity,
        transform: inTail ? `translateX(${style.translateXPct}%) scale(${style.scale})` : undefined,
      }}
    >
      <Freeze frame={Math.max(0, holdFrames - 1)} active={inTail}>
        <ChunkReframe chunk={span.chunk} startFrame={span.startFrame} source={source} outputWidth={width} outputHeight={height}>
          {isPlayer ? <VideoMirror videoRef={videoRef} frozen={inTail} /> : <VideoTrack src={src} muted trimBefore={span.startFrame} />}
        </ChunkReframe>
      </Freeze>
    </AbsoluteFill>
  )
}

/** A canvas repeating the frame of the Player's hidden source <video>; `frozen` keeps the last frame drawn. */
const VideoMirror: React.FC<{ videoRef: React.RefObject<HTMLVideoElement | null>; frozen?: boolean }> = ({ videoRef, frozen = false }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (frozen) return
    let frameId = 0
    let drawnTime = -1
    const draw = () => {
      const video = videoRef.current
      const canvas = canvasRef.current
      if (video && canvas && video.readyState >= 2 && !video.seeking && video.currentTime !== drawnTime) {
        if (canvas.width !== video.videoWidth) canvas.width = video.videoWidth
        if (canvas.height !== video.videoHeight) canvas.height = video.videoHeight
        canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height)
        drawnTime = video.currentTime
      }
      frameId = requestAnimationFrame(draw)
    }
    frameId = requestAnimationFrame(draw)
    return () => cancelAnimationFrame(frameId)
  }, [videoRef, frozen])

  return <canvas ref={canvasRef} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
}

// ---------------------------------------------------------------------------
// Per-chunk reframe renderer
// ---------------------------------------------------------------------------

interface ChunkReframeProps {
  chunk: SceneChunk
  /** The composition frame the chunk's Sequence starts at, to place face keyframes in clip time. */
  startFrame: number
  source: SceneMetadata["source"]
  outputWidth: number
  outputHeight: number
  children: React.ReactNode
}

const ChunkReframe: React.FC<ChunkReframeProps> = ({
  chunk,
  startFrame,
  source,
  outputWidth,
  outputHeight,
  children,
}) => {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const t = (startFrame + frame) / fps
  const faces = chunk.faces?.map((f) => faceAt(f, t))
  switch (chunk.mode) {
    case "wide":
      return (
        <WideMode outputWidth={outputWidth} outputHeight={outputHeight} source={source}>
          {children}
        </WideMode>
      )
    case "single":
      return (
        <SingleMode
          faceCenterXPct={faceCenterXAt(chunk, t)}
          outputWidth={outputWidth}
          outputHeight={outputHeight}
          source={source}
        >
          {children}
        </SingleMode>
      )
    case "dual":
      return (
        <DualMode
          faces={(chunk.faces ?? []).map((f, i) => ({ x: f.x_pct, i })).sort((a, b) => a.x - b.x).map(({ i }) => faces![i])}
          outputWidth={outputWidth}
          outputHeight={outputHeight}
          source={source}
        >
          {children}
        </DualMode>
      )
    case "triple":
      return (
        <TripleMode
          faces={faces ?? []}
          outputWidth={outputWidth}
          outputHeight={outputHeight}
          source={source}
        >
          {children}
        </TripleMode>
      )
    case "quad":
      return (
        <AbsoluteFill style={{ overflow: "hidden", backgroundColor: "#000" }}>
          {layoutPanelRects(chunk, outputWidth / outputHeight).map((rect) => (
            <div
              key={rect.faceIndex}
              style={{
                position: "absolute",
                left: Math.round(rect.x * outputWidth),
                top: Math.round(rect.y * outputHeight),
                width: Math.round(rect.w * outputWidth),
                height: Math.round(rect.h * outputHeight),
                overflow: "hidden",
                boxShadow: "inset 0 0 0 1px #000",
              }}
            >
              <FaceCropPanel
                face={faces?.[rect.faceIndex] ?? { x_pct: 0.2 + 0.2 * rect.faceIndex, y_pct: 0.4, w_pct: 0.08, h_pct: 0.15 }}
                panelWidth={Math.round(rect.w * outputWidth)}
                panelHeight={Math.round(rect.h * outputHeight)}
                source={source}
              >
                {children}
              </FaceCropPanel>
            </div>
          ))}
        </AbsoluteFill>
      )
    case "screenshare": {
      const [pip] = layoutPanelRects(chunk, outputWidth / outputHeight)
      const pipW = Math.round(pip.w * outputWidth)
      const pipH = Math.round(pip.h * outputHeight)
      return (
        <AbsoluteFill>
          <WideMode outputWidth={outputWidth} outputHeight={outputHeight} source={source}>
            {children}
          </WideMode>
          <div
            style={{
              position: "absolute",
              left: Math.round(pip.x * outputWidth),
              top: Math.round(pip.y * outputHeight),
              width: pipW,
              height: pipH,
              overflow: "hidden",
              borderRadius: Math.round(pipW * 0.08),
              border: "4px solid rgba(255,255,255,0.9)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
              boxSizing: "border-box",
            }}
          >
            <FaceCropPanel
              face={faces?.[0] ?? { x_pct: faceCenterXAt(chunk, t), y_pct: 0.4, w_pct: 0.08, h_pct: 0.15 }}
              panelWidth={pipW}
              panelHeight={pipH}
              source={source}
            >
              {children}
            </FaceCropPanel>
          </div>
        </AbsoluteFill>
      )
    }
    default:
      return <AbsoluteFill>{children}</AbsoluteFill>
  }
}

// ---------------------------------------------------------------------------
// Wide mode: full source fit-to-width with blurred letterbox background
// ---------------------------------------------------------------------------

interface WideModeProps {
  outputWidth: number
  outputHeight: number
  source: SceneMetadata["source"]
  children: React.ReactNode
}

const WideMode: React.FC<WideModeProps> = ({ outputWidth, outputHeight, source, children }) => {
  const sourceAspect = source.width / source.height

  // Scale to fit width
  const fitWidth = outputWidth
  const fitHeight = outputWidth / sourceAspect
  const fitTop = (outputHeight - fitHeight) / 2

  return (
    <AbsoluteFill style={{ overflow: "hidden", backgroundColor: "#000" }}>
      {/* Blurred background: stretch to fill entire output */}
      <AbsoluteFill
        style={{
          filter: "blur(30px) brightness(0.4)",
          transform: "scale(1.2)",
        }}
      >
        {children}
      </AbsoluteFill>

      {/* Foreground: scaled to fit width, centered vertically */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: fitTop,
          width: fitWidth,
          height: fitHeight,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: source.width,
            height: source.height,
            transform: `scale(${fitWidth / source.width})`,
            transformOrigin: "0 0",
          }}
        >
          {children}
        </div>
      </div>
    </AbsoluteFill>
  )
}

// ---------------------------------------------------------------------------
// Single mode: height-fill crop centered on face_center_x_pct
// ---------------------------------------------------------------------------

interface SingleModeProps {
  faceCenterXPct: number
  outputWidth: number
  outputHeight: number
  source: SceneMetadata["source"]
  children: React.ReactNode
}

const SingleMode: React.FC<SingleModeProps> = ({
  faceCenterXPct,
  outputWidth,
  outputHeight,
  source,
  children,
}) => {
  // Scale source so height fills the output
  const scale = outputHeight / source.height

  const scaledSourceWidth = source.width * scale

  // Center the face horizontally in the output
  const faceCenterPx = faceCenterXPct * scaledSourceWidth
  let translateX = outputWidth / 2 - faceCenterPx

  // Clamp so we don't show black bars on the sides
  const minTranslateX = outputWidth - scaledSourceWidth
  const maxTranslateX = 0
  translateX = Math.max(minTranslateX, Math.min(maxTranslateX, translateX))

  return (
    <AbsoluteFill style={{ overflow: "hidden" }}>
      <div
        style={{
          width: scaledSourceWidth,
          height: outputHeight,
          transform: `translateX(${translateX}px)`,
          transformOrigin: "0 0",
        }}
      >
        <div
          style={{
            width: source.width,
            height: source.height,
            transform: `scale(${scale})`,
            transformOrigin: "0 0",
          }}
        >
          {children}
        </div>
      </div>
    </AbsoluteFill>
  )
}

// ---------------------------------------------------------------------------
// Dual mode: split-screen top/bottom, each panel zoomed on a face
// ---------------------------------------------------------------------------

interface DualModeProps {
  faces: Array<{ x_pct: number; y_pct: number; w_pct: number; h_pct: number }>
  outputWidth: number
  outputHeight: number
  source: SceneMetadata["source"]
  children: React.ReactNode
}

const DualMode: React.FC<DualModeProps> = ({
  faces,
  outputWidth,
  outputHeight,
  source,
  children,
}) => {
  const panelHeight = outputHeight / 2

  // Faces arrive left to right by their static position, so a panel keeps its person; top = left
  const sorted = faces
  const topFace = sorted[0] ?? { x_pct: 0.33, y_pct: 0.5, w_pct: 0.1, h_pct: 0.15 }
  const bottomFace = sorted[1] ?? sorted[0] ?? { x_pct: 0.66, y_pct: 0.5, w_pct: 0.1, h_pct: 0.15 }

  return (
    <AbsoluteFill style={{ overflow: "hidden", backgroundColor: "#000" }}>
      {/* Thin divider between panels */}
      <div
        style={{
          position: "absolute",
          top: panelHeight - 1,
          left: 0,
          width: outputWidth,
          height: 2,
          backgroundColor: "#000",
          zIndex: 10,
        }}
      />

      {/* Top panel */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: outputWidth,
          height: panelHeight,
          overflow: "hidden",
        }}
      >
        <FaceCropPanel
          face={topFace}
          panelWidth={outputWidth}
          panelHeight={panelHeight}
          source={source}
        >
          {children}
        </FaceCropPanel>
      </div>

      {/* Bottom panel */}
      <div
        style={{
          position: "absolute",
          top: panelHeight,
          left: 0,
          width: outputWidth,
          height: panelHeight,
          overflow: "hidden",
        }}
      >
        <FaceCropPanel
          face={bottomFace}
          panelWidth={outputWidth}
          panelHeight={panelHeight}
          source={source}
        >
          {children}
        </FaceCropPanel>
      </div>
    </AbsoluteFill>
  )
}

// ---------------------------------------------------------------------------
// Triple mode: one on top, two on bottom
// ---------------------------------------------------------------------------

interface TripleModeProps {
  faces: Array<{ x_pct: number; y_pct: number; w_pct: number; h_pct: number }>
  outputWidth: number
  outputHeight: number
  source: SceneMetadata["source"]
  children: React.ReactNode
}

const TripleMode: React.FC<TripleModeProps> = ({
  faces,
  outputWidth,
  outputHeight,
  source,
  children,
}) => {
  const topPanelHeight = Math.round(outputHeight / 2)
  const bottomPanelHeight = outputHeight - topPanelHeight
  const bottomPanelWidth = Math.round(outputWidth / 2)

  const topFace = faces[0] ?? { x_pct: 0.5, y_pct: 0.4, w_pct: 0.08, h_pct: 0.15 }
  const bottomLeftFace = faces[1] ?? { x_pct: 0.33, y_pct: 0.4, w_pct: 0.08, h_pct: 0.15 }
  const bottomRightFace = faces[2] ?? { x_pct: 0.66, y_pct: 0.4, w_pct: 0.08, h_pct: 0.15 }

  return (
    <AbsoluteFill style={{ overflow: "hidden", backgroundColor: "#000" }}>
      {/* Dividers */}
      <div
        style={{
          position: "absolute",
          top: topPanelHeight - 1,
          left: 0,
          width: outputWidth,
          height: 2,
          backgroundColor: "#000",
          zIndex: 10,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: topPanelHeight,
          left: bottomPanelWidth - 1,
          width: 2,
          height: bottomPanelHeight,
          backgroundColor: "#000",
          zIndex: 10,
        }}
      />

      {/* Top panel — full width */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: outputWidth,
          height: topPanelHeight,
          overflow: "hidden",
        }}
      >
        <FaceCropPanel
          face={topFace}
          panelWidth={outputWidth}
          panelHeight={topPanelHeight}
          source={source}
        >
          {children}
        </FaceCropPanel>
      </div>

      {/* Bottom-left panel */}
      <div
        style={{
          position: "absolute",
          top: topPanelHeight,
          left: 0,
          width: bottomPanelWidth,
          height: bottomPanelHeight,
          overflow: "hidden",
        }}
      >
        <FaceCropPanel
          face={bottomLeftFace}
          panelWidth={bottomPanelWidth}
          panelHeight={bottomPanelHeight}
          source={source}
        >
          {children}
        </FaceCropPanel>
      </div>

      {/* Bottom-right panel */}
      <div
        style={{
          position: "absolute",
          top: topPanelHeight,
          left: bottomPanelWidth,
          width: outputWidth - bottomPanelWidth,
          height: bottomPanelHeight,
          overflow: "hidden",
        }}
      >
        <FaceCropPanel
          face={bottomRightFace}
          panelWidth={outputWidth - bottomPanelWidth}
          panelHeight={bottomPanelHeight}
          source={source}
        >
          {children}
        </FaceCropPanel>
      </div>
    </AbsoluteFill>
  )
}

// ---------------------------------------------------------------------------
// Face crop panel: zoom and center on a single face within a panel
// ---------------------------------------------------------------------------

interface FaceCropPanelProps {
  face: { x_pct: number; y_pct: number; w_pct: number; h_pct: number }
  panelWidth: number
  panelHeight: number
  source: SceneMetadata["source"]
  children: React.ReactNode
}

const FaceCropPanel: React.FC<FaceCropPanelProps> = ({
  face,
  panelWidth,
  panelHeight,
  source,
  children,
}) => {
  const crop = faceCropRect(face, panelWidth, panelHeight, source)
  const scale = panelWidth / crop.w
  const translateX = -crop.x * scale
  const translateY = -crop.y * scale

  return (
    <div
      style={{
        width: source.width,
        height: source.height,
        transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
        transformOrigin: "0 0",
      }}
    >
      {children}
    </div>
  )
}
